import { Component, ErrorInfo, ReactNode } from "react";
import i18n from "../../../language/i18n";
import ErrorLayout from "./ErrorLayout";
import { libraryStyles } from "../../../library/styles/libraryStyles";
import { componentStyles } from "../../styles/componentStyles";
import {
  CHUNK_RELOAD_AT_STORAGE_KEY,
  isChunkLoadError,
  shouldReloadForChunkError,
} from "../../../utilization/ChunkLoadErrorUtilization";

interface IAppErrorBoundaryProps {
  children: ReactNode;
}

interface IAppErrorBoundaryState {
  hasError: boolean;
}

/**
 * The app's only error boundary — without it ANY error thrown while rendering unmounts the whole tree and
 * leaves an empty `#root`: a white page with no way forward. Reproduced on prod (Oct 14) by 404ing the lazy
 * chunks, which is what every tab left open across a deploy hits on its next unvisited screen.
 *
 * The route-level `errorElement`s do not cover this: they only work under a data router
 * (`createBrowserRouter`), and this app renders `<BrowserRouter>`, which ignores them.
 *
 * Mounted ABOVE every provider (index.tsx), so the fallback uses no context — only ErrorLayout, whose parts are
 * context-free, plus the style blocks App.tsx would normally have injected (App may be what failed).
 */
class AppErrorBoundary extends Component<
  IAppErrorBoundaryProps,
  IAppErrorBoundaryState
> {
  state: IAppErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): IAppErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught render error", error, errorInfo?.componentStack);
    if (!isChunkLoadError(error)) return;
    const now = Date.now();
    let lastReloadAt: string | null = null;
    try {
      lastReloadAt = window.sessionStorage.getItem(CHUNK_RELOAD_AT_STORAGE_KEY);
    } catch {
      // sessionStorage can throw (privacy mode); without it the loop guard is gone, so do not reload.
      return;
    }
    if (shouldReloadForChunkError(now, lastReloadAt)) {
      window.sessionStorage.setItem(CHUNK_RELOAD_AT_STORAGE_KEY, String(now));
      window.location.reload();
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="appr-root">
        <style>{libraryStyles}</style>
        <style>{componentStyles}</style>
        <div className="brp-center">
          <ErrorLayout
            helpText={i18n.t("unexpected_error_help_text")}
            tone="danger"
            icon="warning"
            actionTitle={i18n.t("refresh")}
            onAction={() => window.location.reload()}
          />
        </div>
      </div>
    );
  }
}

export default AppErrorBoundary;
